import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchGameEvents,
  fetchLiveClaims,
  fetchScoringState,
  fromServerEvent,
  watchClaims,
  watchGame,
  type LiveClaim,
  type ScoringStateRow,
  type ServerEventRow,
} from '../data/live'
import { tally } from '../domain/derive'
import { useAuth } from '../auth/AuthProvider'
import type { StatEvent } from '../domain/types'

/**
 * Watching a game somebody else is scoring.
 *
 * Realtime carries the taps, but it is not trusted on its own. A websocket over
 * stadium wifi drops, reconnects, and occasionally just stops delivering; a
 * tally that has silently stopped moving looks exactly like a quiet patch of
 * the game. So every watcher also polls, and the screen says when it last heard
 * anything -- the alternative is the failure mode this project keeps paying
 * for, where nothing appears to be wrong.
 */

const POLL_MS = 20_000

export interface LiveWatch {
  loading: boolean
  claim: LiveClaim | null
  state: ScoringStateRow | null
  events: StatEvent[]
  counts: Map<string, number>
  connected: boolean
  lastUpdateAt: number | null
  error: string | null
  refresh(): Promise<void>
}

export function useLiveWatch(gameId: string | null): LiveWatch {
  const [state, setState] = useState<ScoringStateRow | null>(null)
  const [rows, setRows] = useState<Map<string, ServerEventRow>>(new Map())
  const [claim, setClaim] = useState<LiveClaim | null>(null)
  const [loading, setLoading] = useState(gameId != null)
  const [connected, setConnected] = useState(false)
  const [lastUpdateAt, setLastUpdateAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)

  const refresh = useCallback(async () => {
    if (!gameId) return
    const [ev, st, claims] = await Promise.all([fetchGameEvents(gameId), fetchScoringState(gameId), fetchLiveClaims()])
    if (!alive.current) return
    if (!ev.ok) {
      setError(ev.error)
      return
    }
    setError(null)
    setRows(new Map(ev.data.map((r) => [r.client_event_id, r])))
    if (st.ok) setState(st.data)
    if (claims.ok) setClaim(claims.data.find((c) => c.game_id === gameId) ?? null)
    setLastUpdateAt(Date.now())
    setLoading(false)
  }, [gameId])

  useEffect(() => {
    alive.current = true
    if (!gameId) {
      setLoading(false)
      return
    }
    setLoading(true)
    void refresh()
    const poll = setInterval(() => void refresh(), POLL_MS)
    const stop = watchGame(gameId, {
      onEvent: (row) => {
        // Merge by client_event_id, the same identity the server merges on, so
        // an update and its insert cannot both count.
        setRows((prev) => {
          const next = new Map(prev)
          next.set(row.client_event_id, row)
          return next
        })
        setLastUpdateAt(Date.now())
      },
      onState: (row) => {
        setState(row)
        setLastUpdateAt(Date.now())
      },
      onClaim: () => void refresh(),
      onStatus: (s) => setConnected(s === 'connected'),
    })
    return () => {
      alive.current = false
      clearInterval(poll)
      stop()
    }
  }, [gameId, refresh])

  const events = useMemo(() => [...rows.values()].map(fromServerEvent), [rows])

  return {
    loading,
    claim,
    state,
    events,
    counts: tally(events),
    connected,
    lastUpdateAt,
    error,
    refresh,
  }
}

/**
 * Is anybody scoring right now?
 *
 * Answered for every signed-in member, so opening the app on a Wednesday night
 * lands on the live tally rather than on a setup screen nobody wants.
 */
export function useLiveNow(): { claim: LiveClaim | null; mine: boolean; refresh(): void } {
  const { session, stage } = useAuth()
  const [claim, setClaim] = useState<LiveClaim | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (stage !== 'ready' || !session) {
      setClaim(null)
      return
    }
    let alive = true
    const load = async () => {
      const res = await fetchLiveClaims()
      if (!alive) return
      // One game at a time in a single-team app; if there were somehow two,
      // the most recently claimed is the one being played now.
      const rows = res.ok ? [...res.data].sort((a, b) => b.claimed_at.localeCompare(a.claimed_at)) : []
      setClaim(rows[0] ?? null)
    }
    void load()
    const poll = setInterval(load, POLL_MS)
    const stop = watchClaims(() => void load())
    return () => {
      alive = false
      clearInterval(poll)
      stop()
    }
  }, [session, stage, tick])

  return {
    claim,
    mine: claim != null && claim.claimed_by === session?.user.id,
    refresh: () => setTick((t) => t + 1),
  }
}
